import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";
import ReviewShareCard, {
  CARD_WIDTH,
  CARD_HEIGHT,
  WORDMARK_LOGO_SRC,
  resolveCardImages,
} from "../ReviewShareCard";
import {
  validateFile,
  downscaleImage,
  requestSignature,
  uploadToCloudinary,
} from "../ReviewHeroUpload";
import { getAuthToken } from "../../utils/authHelpers";

const PhotoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

/**
 * ReviewShareModal — wraps the ReviewShareCard graphic with share actions.
 *
 * "Download image" rasterizes the card DOM to a full-resolution PNG
 * client-side; "Copy link" copies a deep link to the review on the public
 * feed (?review=<id>).
 *
 * Everyone can Download / Copy link. With `canEdit`, the user (a mentioned
 * party, or the super-admin) can also add/replace BOTH the large background
 * photo and the small circle photo — each uploads via the signed Cloudinary
 * endpoint and persists through /api/reviews/set-hero-image.
 *
 * `review` shape (both the Collab Tracker and ReviewsFeed can provide it):
 *   { id, reviewType, reviewText, rating, subjectName, subjectHandle,
 *     reviewerHandle, backgroundImageUrl, circleImageUrl,
 *     subjectAvatarUrl, reviewerAvatarUrl }
 *
 * Modal conventions mirror ApplyModal (backdrop click + Esc close, body
 * scroll lock, inline <style> block, full-screen on mobile).
 */
// `adminPassword` (optional): the admin dashboard's shared password — sent to
// set-hero-image as alternate auth so the admin Review Cards tab can attach
// photos to any review. Photo saves never trigger user notifications.
const ReviewShareModal = ({ isOpen, onClose, review, canEdit = false, onImagesUpdated, adminPassword }) => {
  const exportRef = useRef(null);
  const bgInputRef = useRef(null);
  const circleInputRef = useRef(null);
  const [imagesReady, setImagesReady] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [copied, setCopied] = useState(false);
  // undefined = untouched; string/null = set/cleared in this session
  const [bgOverride, setBgOverride] = useState(undefined);
  const [circleOverride, setCircleOverride] = useState(undefined);
  const [busyTarget, setBusyTarget] = useState(null); // 'background' | 'circle' | null
  const [photoError, setPhotoError] = useState("");

  // The review as currently displayed (including in-session photo changes)
  const effectiveReview = review
    ? {
        ...review,
        backgroundImageUrl: bgOverride !== undefined ? bgOverride : (review.backgroundImageUrl ?? null),
        circleImageUrl: circleOverride !== undefined ? circleOverride : (review.circleImageUrl ?? null),
      }
    : review;

  // Body scroll lock + Esc-to-close, matching ApplyModal.
  useEffect(() => {
    if (!isOpen) return undefined;
    setExportError("");
    setCopied(false);
    setBgOverride(undefined);
    setCircleOverride(undefined);
    setBusyTarget(null);
    setPhotoError("");
    if (typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Preload the exact images the card renders with (CORS-safe Cloudinary
  // URLs) so the export never captures a half-loaded image.
  useEffect(() => {
    if (!isOpen || !effectiveReview) return undefined;
    setImagesReady(false);
    const { bgSrc, circleSrc } = resolveCardImages(effectiveReview);
    const urls = [...new Set([bgSrc, circleSrc, WORDMARK_LOGO_SRC].filter(Boolean))];
    let cancelled = false;
    let remaining = urls.length;
    const done = () => {
      remaining -= 1;
      if (!cancelled && remaining <= 0) setImagesReady(true);
    };
    urls.forEach((url) => {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.onload = done;
      // On error the card falls back gracefully, so unblock the button too.
      img.onerror = done;
      img.src = url;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, review, bgOverride, circleOverride]);

  if (!isOpen || !review) return null;

  const feedPath = review.reviewType === "host" ? "/hosts/reviews" : "/creators/reviews";
  const shareUrl = `${window.location.origin}${feedPath}?review=${review.id}`;

  const handleDownload = async () => {
    if (!imagesReady || exporting || !exportRef.current) return;
    setExporting(true);
    setExportError("");
    try {
      const dataUrl = await toPng(exportRef.current, {
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        pixelRatio: 1,
        cacheBust: false,
      });
      const link = document.createElement("a");
      link.download = `postaway-review-${review.id}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("ReviewShareModal export error:", err);
      setExportError("Couldn't generate the image. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const copyViaTextarea = () => {
    const ta = document.createElement("textarea");
    ta.value = shareUrl;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  };

  const handleCopyLink = async () => {
    let ok = false;
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        ok = true;
      } catch (_) {
        // e.g. document not focused / permission denied — fall through
      }
    }
    if (!ok) {
      try {
        ok = copyViaTextarea();
      } catch (err) {
        console.error("ReviewShareModal copy error:", err);
      }
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Add/replace either image: upload to Cloudinary (signed, same
  // review-heroes folder as the review form), persist the right column on the
  // review, then refresh the card. `target` is 'background' or 'circle'.
  const handlePhotoFile = (target) => async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setPhotoError(validationError);
      return;
    }

    setBusyTarget(target);
    setPhotoError("");
    try {
      const resized = await downscaleImage(file);
      const signature = await requestSignature();
      const uploaded = await uploadToCloudinary(resized, signature);
      const eager = Array.isArray(uploaded.eager) ? uploaded.eager : [];
      const url = eager[0]?.secure_url || uploaded.secure_url;

      const field = target === "circle" ? "circleImageUrl" : "heroImageUrl";
      const token = getAuthToken();
      const res = await fetch("/api/reviews/set-hero-image", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          reviewType: review.reviewType || "creator",
          reviewId: review.id,
          [field]: url,
          ...(adminPassword ? { adminPassword } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Could not save the photo.");
      }

      if (target === "circle") setCircleOverride(url);
      else setBgOverride(url);
      onImagesUpdated?.({ [field]: url });
    } catch (err) {
      console.error("ReviewShareModal photo update error:", err);
      setPhotoError(err.message || "Photo upload failed. Please try again.");
    } finally {
      setBusyTarget(null);
    }
  };

  const cardProps = {
    backgroundImageUrl: effectiveReview.backgroundImageUrl ?? null,
    circleImageUrl: effectiveReview.circleImageUrl ?? null,
    subjectAvatarUrl: review.subjectAvatarUrl ?? null,
    reviewerAvatarUrl: review.reviewerAvatarUrl ?? null,
    reviewText: review.reviewText || "",
    rating: review.rating,
    subjectName: review.subjectName || "",
    subjectHandle: review.subjectHandle ?? null,
    reviewerHandle: review.reviewerHandle ?? null,
    reviewType: review.reviewType || "creator",
  };

  const hasBg = !!cardProps.backgroundImageUrl;
  const hasCircle = !!cardProps.circleImageUrl;
  const downloadLabel = exporting || !imagesReady ? "Preparing..." : "Download image";

  // Portal to <body> so page-level stacking contexts (e.g. the PostcardHero
  // header) can never paint above the modal.
  return createPortal(
    <div
      className="review-share-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Share this review"
      onClick={() => !exporting && onClose?.()}
    >
      <div className="review-share-card-wrap" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="review-share-close"
          aria-label="Close"
          onClick={() => !exporting && onClose?.()}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="review-share-scroll">
          <div className="review-share-body">
            <div className="review-share-eyebrow">Share this review</div>

            <div className="review-share-preview">
              <ReviewShareCard {...cardProps} />
            </div>

            {exportError && <div className="review-share-error">{exportError}</div>}
            {photoError && <div className="review-share-error">{photoError}</div>}

            <div className="review-share-actions">
              <button
                type="button"
                className="review-share-btn primary"
                onClick={handleDownload}
                disabled={exporting || !imagesReady}
              >
                {(exporting || !imagesReady) && <span className="review-share-spinner" aria-hidden />}
                {downloadLabel}
              </button>
              <button
                type="button"
                className="review-share-btn secondary"
                onClick={handleCopyLink}
              >
                {copied ? "Copied!" : "Copy link"}
              </button>
            </div>

            {canEdit && (
              <div className="review-share-edit-row">
                <button
                  type="button"
                  className="review-share-photo-btn"
                  onClick={() => !busyTarget && bgInputRef.current?.click()}
                  disabled={!!busyTarget}
                >
                  {busyTarget === "background" ? (
                    <>
                      <span className="review-share-spinner dark" aria-hidden />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <PhotoIcon />
                      {hasBg ? "Change background photo" : "Add background photo"}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="review-share-photo-btn"
                  onClick={() => !busyTarget && circleInputRef.current?.click()}
                  disabled={!!busyTarget}
                >
                  {busyTarget === "circle" ? (
                    <>
                      <span className="review-share-spinner dark" aria-hidden />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <PhotoIcon />
                      {hasCircle ? "Change circle photo" : "Add circle photo"}
                    </>
                  )}
                </button>
              </div>
            )}

            <input
              ref={bgInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
              style={{ display: "none" }}
              onChange={handlePhotoFile("background")}
            />
            <input
              ref={circleInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
              style={{ display: "none" }}
              onChange={handlePhotoFile("circle")}
            />
          </div>
        </div>
      </div>

      {/* Off-screen, unscaled full-resolution instance used for rasterization
          so the preview transform never affects export quality. */}
      <div className="review-share-export-stage" aria-hidden>
        <ReviewShareCard ref={exportRef} {...cardProps} scaleToFit={false} />
      </div>

      <ReviewShareModalStyles />
    </div>,
    document.body
  );
};

const ReviewShareModalStyles = () => (
  <style>{`
    .review-share-backdrop {
      position: fixed;
      inset: 0;
      z-index: 9999;
      background: rgba(15, 8, 75, 0.55);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      box-sizing: border-box;
      animation: reviewShareFadeIn 180ms ease-out;
      height: 100vh;
      height: 100dvh;
      overflow-y: auto;
    }
    @keyframes reviewShareFadeIn { from { opacity: 0; } to { opacity: 1; } }
    .review-share-card-wrap {
      position: relative;
      background: #ffffff;
      border-radius: 18px;
      max-width: 560px;
      width: 100%;
      max-height: calc(100vh - 48px);
      max-height: calc(100dvh - 48px);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      box-shadow: 0 24px 64px rgba(15, 8, 75, 0.35);
      animation: reviewShareSlideIn 220ms cubic-bezier(0.2, 0.9, 0.3, 1);
      box-sizing: border-box;
    }
    @keyframes reviewShareSlideIn {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    .review-share-scroll {
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    }
    .review-share-close {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: none;
      background: rgba(255, 255, 255, 0.95);
      color: #0f084b;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 10px rgba(15, 8, 75, 0.25);
      transition: background 0.15s, transform 0.15s;
      z-index: 3;
    }
    .review-share-close:hover { background: #ffffff; transform: scale(1.05); }
    .review-share-body {
      padding: 26px 26px 24px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .review-share-eyebrow {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: #dc1d08;
    }
    .review-share-preview {
      display: flex;
      justify-content: center;
      width: 100%;
    }
    /* Cap preview height at ~70vh: width = 70vh * (1080/1920) ≈ 39vh */
    .review-share-preview > * {
      width: 100%;
      max-width: min(100%, 39vh);
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 8px 28px rgba(15, 8, 75, 0.22);
    }
    .review-share-error {
      padding: 10px 12px;
      border-radius: 10px;
      background: rgba(220, 29, 8, 0.08);
      color: #b51706;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
    }
    .review-share-actions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }
    .review-share-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 11px 22px;
      border-radius: 100px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14.5px;
      font-weight: 600;
      border: 2px solid transparent;
      cursor: pointer;
      transition: all 0.18s;
      flex: 1;
      min-height: 44px;
    }
    .review-share-btn.primary {
      background: #dc1d08;
      color: #ffffff;
      border-color: #dc1d08;
    }
    .review-share-btn.primary:hover:not(:disabled) { background: #b51706; border-color: #b51706; }
    .review-share-btn.primary:disabled { background: #e89186; border-color: #e89186; cursor: default; }
    .review-share-btn.secondary {
      background: transparent;
      color: #0f084b;
      border-color: #0f084b;
    }
    .review-share-btn.secondary:hover { background: #0f084b; color: #ffffff; }
    .review-share-spinner {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.45);
      border-top-color: #ffffff;
      animation: reviewShareSpin 700ms linear infinite;
      flex-shrink: 0;
    }
    .review-share-spinner.dark {
      border-color: rgba(15, 8, 75, 0.25);
      border-top-color: #0f084b;
    }
    @keyframes reviewShareSpin { to { transform: rotate(360deg); } }
    .review-share-photo-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      align-self: center;
      padding: 6px 12px;
      border: none;
      background: none;
      color: #0f084b;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      text-decoration: underline;
      text-underline-offset: 3px;
      transition: color 0.15s;
    }
    .review-share-photo-btn:hover:not(:disabled) { color: #dc1d08; }
    .review-share-photo-btn:disabled { cursor: default; text-decoration: none; color: #6b7280; }
    .review-share-edit-row {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 18px;
      justify-content: center;
    }
    .review-share-export-stage {
      position: fixed;
      top: 0;
      left: -12000px;
      pointer-events: none;
    }
    @media (max-width: 640px) {
      .review-share-backdrop { padding: 0; }
      .review-share-card-wrap {
        max-width: 100%;
        width: 100%;
        height: 100vh;
        height: 100dvh;
        max-height: none;
        border-radius: 0;
      }
      .review-share-body { padding: 22px 16px calc(20px + env(safe-area-inset-bottom)); }
      .review-share-preview > * { max-width: min(100%, 38vh); }
      .review-share-actions { flex-direction: column; }
      .review-share-btn { width: 100%; flex: 0 0 auto; }
    }
  `}</style>
);

export default ReviewShareModal;
